import {
  allowsChallengePlacement,
  allowsChallengeTile,
  isFixedTile,
} from '@/challenges/editing';
import { Action, ActionType } from '@/models/HistoryModel';
import { TileInstance, setupDefaultMeta } from '@/models/tiles/TileInstance';
import { deepClone } from '@/utils/clone';
import { TileType } from '@/models/TileTypes';
import { Point, type PointData } from '@/models/Point';
import type {
  IChipEditorModel,
  IConnectionModel,
  ITileManagerModel,
} from '@/interfaces/models';
import { ChipInstanceId } from '@/types/ids';
import Context from '@/context';
import {
  allDisconnected,
  isInterfaceTileDefinition,
  TileMeta,
} from '@/models/tiles/TileDefinition';
import { rotateCCW, rotateCW } from '@/models/data/Rotation';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import { getEffectiveSideIO } from '@/models/tiles/TileDefinition';
import type { Connection, ConnectionPoint } from '@/models/connections/ConnectionGraph';
import { InterfaceTileState, Side, TileSideIOType } from '@/schemas/tiles';
import { Rotation } from '@/models/data/Rotation';
import { CircuitType, oppositeCircuitType } from '@/schemas/common';
import { SIInputMode } from '@/models/ChipModel';
import type { SingleInterfaceTileState } from '@/models/tiles/SingleInterfaceTile';
import { resolveConnectionSides } from '@/models/logic/utils';
import { InterfaceLinkInstanceId } from '@/types/ids';
import { withInterfaceLabelAllocation } from '@/models/tiles/interfaceLabelAllocation';

export interface TileInfo {
  tileType?: TileType;
  location: Point;
  meta: TileMeta;
  state?: unknown;
  /** Preferred fresh label from the gesture preview; placement still checks uniqueness. */
  interfaceLabel?: string;
}

export interface PlaceTilesConnectionRequest {
  a: PointData;
  b: PointData;
}

interface PlaceTilesActionOptions {
  connectionModel: IConnectionModel;
  connectionRequests: PlaceTilesConnectionRequest[];
}

export function addInterfaceTileToChipModel(
  placedTile: TileInstance,
  chipEditor: IChipEditorModel,
) {
  const tileDefinition = tileDefinitionRegistry.get(placedTile.tileType);
  if (
    isInterfaceTileDefinition(tileDefinition) ||
    (placedTile.tileType === TileType.BundleInterface &&
      (placedTile.state as { connectedInterface?: unknown }).connectedInterface)
  ) {
    const chipModel = chipEditor.chipModel;
    const state = placedTile.state as InterfaceTileState<unknown>;
    const linkId = state.connectedInterface.interfaceLinkId as InterfaceLinkInstanceId;

    if (linkId) {
      const link = chipModel.interfaceLinkMap.get(linkId);
      const counterpart = link?.tiles[oppositeCircuitType(chipEditor.editingMode)];
      if (
        placedTile.tileType === TileType.SingleInterface &&
        counterpart?.tileType === TileType.SingleInterface &&
        (link?.inputMode === SIInputMode.LEVER || link?.inputMode === SIInputMode.BUTTON)
      ) {
        // Sample at attachment, including redo: a cached placement may be stale.
        // Initialize only the incoming control; never reset its live counterpart.
        const incoming = placedTile.state as SingleInterfaceTileState;
        const current = counterpart.state as SingleInterfaceTileState;
        if (
          incoming.inputMode !== link.inputMode ||
          incoming.toggleState !== current.toggleState
        ) {
          incoming.inputMode = link.inputMode;
          incoming.toggleState = current.toggleState;
          placedTile.renderVersion++;
        }
      }
      chipModel.addInterfaceTile(linkId, placedTile, chipEditor.editingMode);
    }
  }
}

export function removeInterfaceTileFromChipModel(
  removedTile: TileInstance,
  chipEditor: IChipEditorModel,
) {
  const tileDefinition = tileDefinitionRegistry.get(removedTile.tileType);
  if (
    isInterfaceTileDefinition(tileDefinition) ||
    (removedTile.tileType === TileType.BundleInterface &&
      (removedTile.state as { connectedInterface?: unknown }).connectedInterface)
  ) {
    const chipModel = chipEditor.chipModel;
    const state = removedTile.state as InterfaceTileState<unknown>;
    const linkId = state.connectedInterface.interfaceLinkId as InterfaceLinkInstanceId;

    if (linkId) {
      chipModel.removeInterfaceTile(linkId, chipEditor.editingMode);
    }
  }
}

export function createPlaceTileAction(
  context: Context,
  selectedTileType: TileType,
  location: Point,
  chipInstanceId: ChipInstanceId,
  tileManager: ITileManagerModel,
  circuitType: CircuitType,
): Action {
  return createPlaceTilesAction(
    context,
    selectedTileType,
    [{ location, meta: setupDefaultMeta() }],
    chipInstanceId,
    tileManager,
    circuitType,
  );
}

export function createPlaceTilesAction(
  context: Context,
  selectedTileType: TileType,
  tileInfos: TileInfo[],
  chipInstanceId: ChipInstanceId,
  tileManager: ITileManagerModel,
  circuitType: CircuitType,
  options?: PlaceTilesActionOptions,
): Action {
  const originalInterfaceIds = new Set(
    context.challengeAuthoring
      ? context.editor?.currentChipEditor?.chipModel?.interfaceLinks?.map(
          (link) => link.id,
        )
      : [],
  );
  tileInfos = tileInfos.filter(
    (info) =>
      allowsChallengePlacement(context, tileManager, info.location) &&
      allowsChallengeTile(
        context,
        tileManager,
        info.tileType ?? selectedTileType,
        info.state,
      ),
  );
  if (options)
    options = {
      ...options,
      connectionRequests: options.connectionRequests.filter(
        ({ a, b }) =>
          allowsChallengePlacement(context, tileManager, a) &&
          allowsChallengePlacement(context, tileManager, b) &&
          !(
            tileManager.get(Point.fromData(a)) &&
            tileManager.get(Point.fromData(b)) &&
            isFixedTile(tileManager.get(Point.fromData(a))!) &&
            isFixedTile(tileManager.get(Point.fromData(b))!)
          ),
      ),
    };
  let placedTiles: TileInstance[] = [];
  let appliedConnections: Connection[] = [];
  let cachedTiles: TileInstance[] | null = null;
  let cachedInterfaceLinks: Map<
    InterfaceLinkInstanceId,
    { link: unknown; linkId: InterfaceLinkInstanceId }
  > | null = null;

  const connectTilesAtPositions = (
    connectionModel: IConnectionModel,
    request: PlaceTilesConnectionRequest,
  ): Connection | null => {
    const pointAObject = Point.fromData(request.a);
    const pointBObject = Point.fromData(request.b);

    if (!pointAObject.isNeighbor(pointBObject)) {
      return null;
    }

    const tileA = tileManager.get(pointAObject);
    const tileB = tileManager.get(pointBObject);

    if (!tileA || !tileB) {
      return null;
    }

    const sides = resolveConnectionSides(tileA, tileB);

    if (!sides) {
      return null;
    }
    const { sideA, sideB } = sides;

    const definitionA = tileDefinitionRegistry.get(tileA.tileType);
    const definitionB = tileDefinitionRegistry.get(tileB.tileType);

    if (
      getEffectiveSideIO(tileA, sideA, definitionA).type === TileSideIOType.NONE ||
      getEffectiveSideIO(tileB, sideB, definitionB).type === TileSideIOType.NONE
    ) {
      return null;
    }

    const existingConnection = connectionModel.connectionsFor(tileA.id).get(sideA);
    if (
      existingConnection &&
      existingConnection.tileId === tileB.id &&
      existingConnection.side === sideB
    ) {
      return null;
    }

    const connectionPointA: ConnectionPoint = { tileId: tileA.id, side: sideA };
    const connectionPointB: ConnectionPoint = { tileId: tileB.id, side: sideB };

    connectionModel.connect(connectionPointA, connectionPointB);
    return { a: connectionPointA, b: connectionPointB };
  };

  return {
    type: ActionType.AddTiles,
    isNoop: tileInfos.length === 0 && !options?.connectionRequests.length,
    description: 'Place Tiles',
    do: () => {
      placedTiles = [];
      appliedConnections = [];

      if (cachedTiles) {
        // Redo path: re-add cached tiles and restore interface links
        for (const tile of cachedTiles) {
          tileManager.add(tile);
          placedTiles.push(tile);

          if (context.editor?.currentChipEditor) {
            const chipEditor = context.editor.currentChipEditor;
            const tileDefinition = tileDefinitionRegistry.get(tile.tileType);
            if (isInterfaceTileDefinition(tileDefinition)) {
              const state = tile.state as InterfaceTileState<unknown>;
              const linkId = state.connectedInterface
                .interfaceLinkId as InterfaceLinkInstanceId;
              if (linkId && cachedInterfaceLinks?.has(linkId)) {
                const cached = cachedInterfaceLinks.get(linkId)!;
                chipEditor.chipModel.interfaceLinkMap.set(
                  linkId,
                  cached.link as import('@/models/ChipModel').InterfaceLink,
                );
              }
              addInterfaceTileToChipModel(tile, chipEditor);
            }
          }
        }
      } else {
        // First run: create fresh tiles
        const hasFreshInterface = tileInfos.some((info) => {
          const type = info.tileType ?? selectedTileType;
          return type === TileType.SingleInterface || type === TileType.MultiInterface;
        });
        withInterfaceLabelAllocation(
          hasFreshInterface ? context.editor?.currentChipEditor?.chipModel : undefined,
          () => {
            for (const info of tileInfos) {
              if (
                !allowsChallengeTile(
                  context,
                  tileManager,
                  info.tileType ?? selectedTileType,
                  info.state,
                )
              )
                continue;
              const placedTile = new TileInstance(
                context,
                info.tileType ?? selectedTileType,
                {
                  chipInstanceId,
                  location: info.location,
                  circuitType,
                },
              );
              placedTile.meta = info.meta;
              if (info.state !== undefined) {
                placedTile.state = deepClone(info.state) as typeof placedTile.state;
              }
              placedTiles.push(placedTile);
              tileManager.add(placedTile);

              if (!context.editor?.currentChipEditor) {
                continue;
              }
              const tileDefinition = tileDefinitionRegistry.get(placedTile.tileType);

              tileDefinition?.eventHandlers?.placed?.({
                instance: placedTile,
                interfaceLabel: info.interfaceLabel,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                chipEditorModel: context.editor!.currentChipEditor,
              });
            }
          },
        );
        // Cache tiles for redo
        cachedTiles = [...placedTiles];
      }

      if (options && options.connectionRequests.length > 0) {
        const connectRequests = () => {
          const seen = new Set<string>();
          for (const request of options.connectionRequests) {
            const key = `${request.a.x},${request.a.y}|${request.b.x},${request.b.y}`;
            const reverseKey = `${request.b.x},${request.b.y}|${request.a.x},${request.a.y}`;

            if (seen.has(key) || seen.has(reverseKey)) {
              continue;
            }

            const connection = connectTilesAtPositions(options.connectionModel, request);
            if (connection) {
              appliedConnections.push(connection);
              seen.add(key);
              seen.add(reverseKey);
            }
          }
        };
        if (options.connectionModel.batchConnections)
          options.connectionModel.batchConnections(connectRequests);
        else connectRequests();
        appliedConnections = appliedConnections.filter(({ a, b }) => {
          const accepted = options.connectionModel.connectionsFor(a.tileId).get(a.side);
          return accepted?.tileId === b.tileId && accepted.side === b.side;
        });
      }

      if (placedTiles.length || appliedConnections.length)
        context.app?.playSound?.('place');
    },
    undo: () => {
      if (options?.connectionModel && appliedConnections.length > 0) {
        for (const connection of appliedConnections) {
          options.connectionModel.disconnect(connection.a, connection.b, {
            preventReconnect: true,
          });
        }
        appliedConnections = [];
      }

      if (placedTiles.length > 0) {
        // Cache interface links before removal
        if (!cachedInterfaceLinks && context.editor?.currentChipEditor) {
          cachedInterfaceLinks = new Map();
          const chipEditor = context.editor.currentChipEditor;
          for (const tile of placedTiles) {
            const tileDefinition = tileDefinitionRegistry.get(tile.tileType);
            if (isInterfaceTileDefinition(tileDefinition)) {
              const state = tile.state as InterfaceTileState<unknown>;
              const linkId = state.connectedInterface
                .interfaceLinkId as InterfaceLinkInstanceId;
              if (linkId) {
                const link = chipEditor.chipModel.interfaceLinkMap.get(linkId);
                if (link) {
                  cachedInterfaceLinks.set(linkId, { link, linkId });
                }
              }
            }
          }
        }

        for (const placedTile of placedTiles) {
          tileManager.remove(placedTile);

          if (!context.editor?.currentChipEditor) {
            continue;
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          removeInterfaceTileFromChipModel(placedTile, context.editor!.currentChipEditor);
          const linkId = (placedTile.state as InterfaceTileState<unknown>)
            ?.connectedInterface?.interfaceLinkId as InterfaceLinkInstanceId | undefined;
          if (context.challengeAuthoring && linkId && !originalInterfaceIds.has(linkId))
            context.editor.currentChipEditor.chipModel.interfaceLinkMap.delete(linkId);
        }
        placedTiles = [];
      }
    },
  };
}

export function createRemoveTilesAction(
  context: Context,
  tileManager: ITileManagerModel,
  tiles: TileInstance[],
  options?: { connectionModel?: IConnectionModel },
): Action {
  const tilesToRemove = tiles.filter((tile) => !isFixedTile(tile));
  const removedInterfaceLinks = new Map<
    InterfaceLinkInstanceId,
    import('@/models/ChipModel').InterfaceLink
  >();
  const description = tilesToRemove.length === 1 ? 'Remove Tile' : 'Remove Tiles';
  let removedRecords: Array<{ tile: TileInstance; connections: Connection[] }> = [];

  const makeConnectionKey = (connection: Connection): string => {
    const keyA = `${connection.a.tileId}:${connection.a.side}`;
    const keyB = `${connection.b.tileId}:${connection.b.side}`;
    return keyA < keyB ? `${keyA}|${keyB}` : `${keyB}|${keyA}`;
  };

  return {
    type: ActionType.RemoveTiles,
    isNoop: tilesToRemove.length === 0,
    description,
    do: () => {
      removedRecords = [];
      removedInterfaceLinks.clear();
      for (let index = 0; index < tilesToRemove.length; index++) {
        const tile = tilesToRemove[index];
        const locationPoint = Point.fromData(tile.location);
        const existing = tileManager.get(locationPoint);

        if (!existing || isFixedTile(existing)) {
          continue;
        }

        if (existing !== tile) {
          tilesToRemove[index] = existing;
        }

        const record: { tile: TileInstance; connections: Connection[] } = {
          tile: tilesToRemove[index],
          connections: [],
        };

        if (options?.connectionModel) {
          const connectionEntries = options.connectionModel.connectionsFor(
            tilesToRemove[index].id,
          );
          for (const [side, target] of connectionEntries) {
            const connection: Connection = {
              a: { tileId: tilesToRemove[index].id, side },
              b: target,
            };
            record.connections.push(connection);
            options.connectionModel.disconnect(connection.a, connection.b);
          }
        }

        if (
          context.editor?.currentChipEditor &&
          !existing.meta.partOfChip &&
          isInterfaceTileDefinition(tileDefinitionRegistry.get(existing.tileType))
        ) {
          const id = (existing.state as InterfaceTileState<unknown>).connectedInterface
            .interfaceLinkId as InterfaceLinkInstanceId;
          const link =
            context.editor?.currentChipEditor?.chipModel.interfaceLinkMap.get(id);
          if (link) removedInterfaceLinks.set(id, link);
        }
        tileManager.remove(existing);
        removedRecords.push(record);
        if (!context.editor?.currentChipEditor) {
          continue;
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        removeInterfaceTileFromChipModel(existing, context.editor!.currentChipEditor);
      }
      if (removedRecords.length) context.app?.playSound?.('remove');
    },
    undo: () => {
      const connectionModel = options?.connectionModel;
      const chip = context.editor?.currentChipEditor?.chipModel;
      if (chip)
        for (const [id, link] of removedInterfaceLinks) {
          if (!chip.interfaceLinkMap.has(id)) chip.interfaceLinkMap.set(id, link);
        }
      for (const tile of tilesToRemove) {
        const locationPoint = Point.fromData(tile.location);
        const occupyingTile = tileManager.get(locationPoint);
        if (occupyingTile && occupyingTile.id !== tile.id) {
          tileManager.remove(occupyingTile);
        }

        tileManager.add(tile);

        if (!context.editor?.currentChipEditor) {
          continue;
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        addInterfaceTileToChipModel(tile, context.editor!.currentChipEditor);
      }

      if (connectionModel) {
        const seen = new Set<string>();
        for (const record of removedRecords) {
          for (const connection of record.connections) {
            const key = makeConnectionKey(connection);
            if (seen.has(key)) {
              continue;
            }

            const tileAExists = tileManager.tiles.some(
              (tileInstance) => tileInstance.id === connection.a.tileId,
            );
            const tileBExists = tileManager.tiles.some(
              (tileInstance) => tileInstance.id === connection.b.tileId,
            );

            if (!tileAExists || !tileBExists) {
              continue;
            }

            connectionModel.connect(connection.a, connection.b);
            seen.add(key);
          }
        }
      }
    },
  };
}

export function createRotateTileAction(
  tile: TileInstance,
  tileManager: ITileManagerModel,
  connectionModel?: IConnectionModel,
  direction: 'CW' | 'CCW' = 'CW',
): Action {
  const position = tile.globalTilePosition;
  const oldRotation = tile.meta.rotation;
  const connectionFlags = connectionModel?.connectionFlags(tile.id) ?? {
    ...allDisconnected,
  };
  const hasConnections = Object.values(connectionFlags).some(Boolean);
  const tileDefinition = tileDefinitionRegistry.get(tile.tileType);
  const canRotate = tileDefinition.canRotate;

  // For non-rotatable tiles, store connections to restore on undo
  let brokenConnections: Map<Side, ConnectionPoint> = new Map();

  // Determine action description and behavior
  let description = 'Rotate Tile';

  if (!canRotate) {
    if (tile.tileType === TileType.Bridge) {
      // Bridge keeps connections, no action needed
      description = 'Bridge - No Rotation';
    } else if (hasConnections) {
      // Non-rotatable tiles (except Bridge): break connections
      description = `Break ${tile.tileType} Connections`;
      // Store current connections before breaking
      brokenConnections = connectionModel?.connectionsFor(tile.id) ?? new Map();
    } else {
      // No connections to break, no action
      description = `${tile.tileType} - No Action`;
    }
  }

  return {
    type: ActionType.RotateTile,
    isNoop: isFixedTile(tile),
    description,
    do: () => {
      // Look up the current tile at this position
      const currentTile = tileManager.get(Point.fromData(position.location));
      if (!currentTile || isFixedTile(currentTile)) return;

      if (!canRotate) {
        if (currentTile.tileType !== TileType.Bridge && hasConnections) {
          connectionModel?.breakOptionalConnections(currentTile.id);
        }
      } else {
        // Rotatable tiles: remove all connections (including manual ones)
        // before rotating, then the reaction will rebuild forced connections
        connectionModel?.removeConnectionsForTile(currentTile.id);

        // Rotate the tile
        currentTile.meta.rotation =
          direction === 'CW'
            ? rotateCW(currentTile.meta.rotation as Rotation)
            : rotateCCW(currentTile.meta.rotation as Rotation);
        // Note: ConnectionModel's reaction will handle recalculating forced connections
      }
    },
    undo: () => {
      // Look up the current tile at this position
      const currentTile = tileManager.get(Point.fromData(position.location));
      if (!currentTile || isFixedTile(currentTile)) return;

      if (!canRotate) {
        if (currentTile.tileType !== TileType.Bridge && brokenConnections.size > 0) {
          // Re-connect the broken connections
          for (const [side, connPoint] of brokenConnections) {
            connectionModel?.connect({ tileId: currentTile.id, side }, connPoint);
          }
        }
      } else {
        // Rotatable tiles: remove all connections before restoring rotation
        connectionModel?.removeConnectionsForTile(currentTile.id);

        // Restore rotation
        currentTile.meta.rotation = oldRotation;
        // Note: ConnectionModel's reaction will handle recalculating forced connections
      }
    },
  };
}
